import apiClient from "../../client/apiClient";
import { appendQueryParams } from "../../utils";

const ENDPOINT = "/operating-days";

export type OperatingDay = {
  _id: string;
  branch?: string | { _id: string; name?: string; campusNo?: string };
  branchId?: string | { _id: string; name?: string; campusNo?: string };
  date: string;
  status?: string;
  reportedBy?: string | { _id: string; firstName?: string; lastName?: string; email?: string };
  note: string;
  givenAt?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type OperatingStatus = "PENDING" | "OPENED" | "CLOSED";

export type OpenedForRef = { _id: string; firstName?: string; lastName?: string; name?: string };

export type OpenedClassSection = {
  class: OpenedForRef & { stage?: string; classGroup?: { _id: string; name: string } | null };
  section: OpenedForRef;
};

export type OperatingDayFull = OperatingDay & {
  status?: OperatingStatus;
  session?: string;
  openedFor?: {
    staff?: OpenedForRef[];
    // Legacy deduped section ids — a Section document is commonly shared
    // across many classes, so this alone can't say which class it was
    // opened under. classSections (below) is the accurate source; only
    // records saved before that field existed lack it.
    sections?: OpenedForRef[];
    classSections?: OpenedClassSection[];
  };
  openedBy?: OpenedForRef | string;
  openedAt?: string;
  closedBy?: OpenedForRef | string;
  closedAt?: string;
  autoClosed?: boolean;
};

export type TodayResponse = {
  date: string;
  day: string;
  status: OperatingStatus;
  record: OperatingDayFull | null;
};

/** GET /operating-days/today — today's status for the selected campus (x-branch). */
export const getToday = async (): Promise<TodayResponse> => {
  const response = await apiClient({ url: `${ENDPOINT}/today`, method: "GET" });
  return response.data;
};

export type StaffOption = {
  _id: string;
  firstName?: string;
  lastName?: string;
  role?: { _id: string; name: string } | string;
};

/**
 * GET /operating-days/staff-options — Section 1's staff list, scoped to
 * this feature's own permission. Deliberately NOT the general getAllUsers
 * (/auth/users) — that requires the broad "user.view" permission, which a
 * role scoped only to School Opening legitimately doesn't have.
 */
export const getStaffOptions = async (): Promise<{ data: StaffOption[] }> => {
  const response = await apiClient({ url: `${ENDPOINT}/staff-options`, method: "GET" });
  return response.data;
};

/** POST /operating-days/open — open the school for the selected staff/sections. */
export const openSchool = async (payload: {
  staff: string[];
  sections: string[];
  classSections: { class: string; section: string }[];
  sessionId?: string;
  note?: string;
}): Promise<OperatingDayFull> => {
  const response = await apiClient({ url: `${ENDPOINT}/open`, method: "POST", data: payload });
  return response.data;
};

/** POST /operating-days/close — close the school for the day. */
export const closeSchool = async (payload: {
  sessionId?: string;
  note?: string;
}): Promise<OperatingDayFull> => {
  const response = await apiClient({ url: `${ENDPOINT}/close`, method: "POST", data: payload });
  return response.data;
};

export type GetByDateResponse = OperatingDay | { date: string; branch: string; isOpen: false };

export type ListResponse = {
  data: OperatingDay[];
  pagination: { page: number; limit: number; total: number; pages: number };
};

/** GET /branch-daily-opening/check?date=YYYY-MM-DD — status for current user's branch */
export const getByDate = async (date: string , branchId: string): Promise<GetByDateResponse> => {
  const response = await apiClient({
    url: appendQueryParams(`${ENDPOINT}/check`, { date, branchId }),
    method: "GET",
  });
  return response.data;
};

/** GET — query: startDate, endDate, page, limit, branchId */
export const list = async (params: {
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  branchId?: string;
}): Promise<ListResponse> => {
  const response = await apiClient({
    url: appendQueryParams(ENDPOINT, {
      startDate: params.startDate,
      endDate: params.endDate,
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      branchId: params.branchId,
    }),
    method: "GET",
  });
  const body = response.data;
  return {
    data: body.data ?? [],
    pagination: body.pagination ?? { page: 1, limit: 10, total: 0, pages: 0 },
  };
};

/** POST — body: { date, branchId, sessionId?, isOpen?, note? } */
export const create = async (payload: {
  date: string;
  branchId: string;
  sessionId?: string;
  status?: string;
  note?: string;
}): Promise<OperatingDay> => {
  const response = await apiClient({
    url: ENDPOINT,
    method: "POST",
    data: {
      date: payload.date,
      branchId: payload.branchId,
      sessionId: payload.sessionId,
      status: payload.status,
      note: payload.note,
    },
  });
  return response.data;
};

/** GET /branch-daily-opening/:id */
export const getById = async (id: string): Promise<OperatingDay> => {
  const response = await apiClient({
    url: `${ENDPOINT}/${id}`,
    method: "GET",
  });
  return response.data;
};

/** PUT /branch-daily-opening/:id — body: { isOpen?, note? } */
export const update = async (
  id: string,
  payload: { status?: string; note?: string }
): Promise<OperatingDay> => {
  const response = await apiClient({
    url: `${ENDPOINT}/${id}`,
    method: "PUT",
    data: payload,
  });
  return response.data;
};

/** DELETE /branch-daily-opening/:id */
export const remove = async (id: string): Promise<{ deleted: boolean; id: string }> => {
  const response = await apiClient({
    url: `${ENDPOINT}/${id}`,
    method: "DELETE",
  });
  return response.data;
};
